import { useLingui } from "@lingui/react/macro";
import { Button } from "@orbin/ui-web";
import { X } from "lucide-react";
import { useState } from "react";
import type { Allowance } from "../lib/allowance";
import { useAllowance } from "../lib/allowance";
import { useBilling } from "./SubscriptionGate";

const STORAGE_KEY = "prea.allowance-banner-dismissed";

function dismissalKey(allowance: Allowance) {
  return `${allowance.periodEnd}:${allowance.level}`;
}

function readDismissed(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Above the composer when the period's allowance is nearly or fully used; dismissed until the next period. */
export function AllowanceBanner() {
  const { t } = useLingui();
  const allowance = useAllowance();
  const { showPlans } = useBilling();
  const [dismissed, setDismissed] = useState(readDismissed);
  if (!allowance || allowance.level === "ok") return null;
  const key = dismissalKey(allowance);
  if (dismissed === key) return null;
  const full = allowance.level === "full";
  const message = !full
    ? t`Your plan is close to its limit.`
    : allowance.dimension === "computer_minutes"
      ? t`Computer time is used up. Preá can't use the computer until your plan renews.`
      : allowance.dimension === "voice_minutes"
        ? t`Voice time is used up. Calls and dictation pause until your plan renews.`
        : t`Task credits are used up. Preá can't start new tasks until your plan renews.`;
  return (
    <div
      role="status"
      data-testid="allowance-banner"
      data-allowance-banner=""
      data-level={allowance.level}
      className="peer rounded-t-[26px] border border-b-0 border-border bg-background"
    >
      <div
        className={`flex items-center gap-2 rounded-t-[26px] px-4 py-2 text-body2 ${
          full ? "bg-destructive/10 text-destructive" : "bg-warning/10 text-warning"
        }`}
      >
        <span className="min-w-0 flex-1">{message}</span>
        <Button size="xs" variant="outline" onClick={showPlans}>
          {t`See plans`}
        </Button>
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label={t`Dismiss`}
          onClick={() => {
            try {
              localStorage.setItem(STORAGE_KEY, key);
            } catch {
              // Dismissal then lasts until the page reloads.
            }
            setDismissed(key);
          }}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}
